import { useCallback, useEffect, useRef, useState } from "react";
import {
  startVerification,
  devCompleteVerification,
  getMe,
  ApiError,
  type PublicUser,
} from "./api.ts";
import { openLink } from "./telegram.ts";

type GateMode = "intro" | "starting" | "pending" | "failed";

const POLL_INTERVAL_MS = 3000;

/**
 * Hard age gate (CLAUDE.md §5). No products render until the backend reports a
 * `verified` status. Starts verification, opens the provider flow (if hosted),
 * and polls `/api/me` for the async decision. In the mock/dev config it also
 * offers a shortcut to simulate approval.
 */
export function AgeGate({
  user,
  onVerified,
}: {
  user: PublicUser;
  onVerified: (user: PublicUser) => void;
}) {
  const [mode, setMode] = useState<GateMode>(
    user.verificationStatus === "pending"
      ? "pending"
      : user.verificationStatus === "failed"
        ? "failed"
        : "intro",
  );
  const [error, setError] = useState<string | null>(null);
  const [devAvailable, setDevAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      try {
        const me = await getMe();
        if (me.verificationStatus === "verified") {
          stopPolling();
          onVerified(me);
        } else if (me.verificationStatus === "failed") {
          stopPolling();
          setMode("failed");
        }
      } catch {
        // transient; keep polling
      }
    }, POLL_INTERVAL_MS);
  }, [onVerified, stopPolling]);

  useEffect(() => {
    if (mode === "pending") startPolling();
    return stopPolling;
  }, [mode, startPolling, stopPolling]);

  const handleStart = async () => {
    setError(null);
    setMode("starting");
    try {
      const res = await startVerification();
      setDevAvailable(res.devComplete);
      if (res.status === "verified") {
        onVerified(await getMe());
        return;
      }
      if (res.redirectUrl) openLink(res.redirectUrl);
      setMode("pending");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not start");
      setMode("intro");
    }
  };

  const handleDevComplete = async () => {
    setBusy(true);
    setError(null);
    try {
      const me = await devCompleteVerification();
      stopPolling();
      onVerified(me);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "dev-complete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card gate">
      <h2 className="gate-title">Age verification required</h2>
      <p className="gate-text">
        This is an age-restricted store. You must verify your identity and that
        you are of legal age (21+) before you can browse products.
      </p>

      {error && <p className="note error">{error}</p>}

      {mode === "intro" && (
        <button className="btn" onClick={handleStart}>
          Verify my age
        </button>
      )}

      {mode === "starting" && (
        <button className="btn" disabled>
          Starting…
        </button>
      )}

      {mode === "pending" && (
        <>
          <p className="gate-text pending">
            Verification in progress… this updates automatically once your
            provider responds.
          </p>
          {devAvailable && (
            <button className="btn secondary" onClick={handleDevComplete} disabled={busy}>
              {busy ? "Approving…" : "Simulate approval (dev)"}
            </button>
          )}
        </>
      )}

      {mode === "failed" && (
        <>
          <p className="note error">
            Verification did not pass. You cannot place orders until you are
            verified.
          </p>
          <button className="btn" onClick={handleStart}>
            Try again
          </button>
        </>
      )}
    </section>
  );
}
